import { Button } from '@/components/ui/button';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import {
  deleteChatConsent,
  deleteChatReady,
  getChatConsent,
  postChatConsent,
  postChatReady,
} from '@/lib/api';
import {
  CHAT_PEER_DISCONNECTED,
  CHAT_PEER_WAITING,
  CHAT_ROLE_LABELS,
  chatConsentQueryKey,
  chatLobbySurface,
  chatRoomQueryKey,
} from '@/lib/chat-lobby';
import type { ChatRoomView } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface ChatLobbyProps {
  /** In the PATH and never in the query (§5) - it is what every gesture is sent with. */
  token: string;
  room: ChatRoomView;
}

/**
 * The lobby, S1 and S2 (chat spec §3, §4, §6) - ONE component for both sides.
 *
 * `room.role` decides the sentences. Never a second component and never a prop
 * read from the URL: the role is resolved server-side (§5), which is what keeps
 * the interviewer's controls out of the guest's DOM entirely rather than merely
 * hidden in it - and here it is also what decides which consent block the
 * server served.
 *
 * ## What is decided elsewhere, and why none of it is here
 *
 *   - **The six doors** are `chat/lobby.ts` on the backend, pure. This screen
 *     never decides that a conversation opens; it notices that the room's
 *     status moved and the shell above it swaps the phase.
 *   - **The cadence and the wording rule** are `lib/chat-lobby.ts`, pure and
 *     tested. What is left in this file is markup and four gestures.
 *   - **The consent text** is read from base and SERVED (§6, BUILD-829
 *     régime), so a rewording reaches the next guest instead of the next deploy
 *     of the bundle. Nothing here imports a wording, and the box renders no
 *     label until the text has landed: a checkbox above a blank paragraph is
 *     not a consent, the rule `routes/index.tsx` already holds.
 *   - **Which gestures may be undone**, and in what order, is the server's
 *     (§4.2's régime, both directions): `DELETE /ready` always, `DELETE
 *     /consent` only while this role is not ready. The `disabled` on the box
 *     below mirrors that refusal; it does not constitute it.
 *
 * ## The shape of the screen, and why the box sits where it does (issue #21)
 *
 * Four gestures, but only TWO of them are what the person came here to do, and
 * they now live in one bordered block at the bottom: the box that authorises,
 * then the button that commits. They used to be separated by the presence panel
 * and by a full-width « en direct » encart, which put the button below the fold
 * of a laptop and left the reason it was dark a scroll away from it. The encart
 * is one line inside that block now - it is a condition of pressing the button,
 * not a decoration of the page - and the vertical rhythm went from `gap-8
 * py-12` to `gap-5 py-6`.
 *
 * The presence panel STAYS, and stays legible: §4 requires a disconnection to
 * be visible, so tightening this screen was never allowed to hide it.
 *
 * ## Why a refusal cannot be read off this screen (§6)
 *
 * The projection carries no `consentedAt` for the other side, so the three
 * reasons she might not be ready - never opened her link, opened it and did not
 * tick, ticked and has not pressed the button - are one state here. Presence is
 * a separate fact and IS shown, because §4 requires a disconnection to be
 * visible and §11 needs « parti » told from « en train de composer » ; someone
 * who arrived and did not tick keeps polling, so she reads « connecté » like
 * everybody else.
 *
 * ## No clock of its own
 *
 * Presence is measured against `Date.now()` read at RENDER, and there is no
 * interval behind it: the poll is the clock. Every ~1500 ms a new answer
 * re-renders this component, which is precisely the resolution the presence
 * window is written for - and a second timer would be a second cadence to keep
 * in step with the first.
 */
export function ChatLobby({ token, room }: ChatLobbyProps) {
  const queryClient = useQueryClient();
  const consentText = useQuery({
    queryKey: chatConsentQueryKey(token),
    queryFn: () => getChatConsent(token),
    // The wording moves a handful of times a year and this screen lives for
    // minutes: re-asking for it on every focus would be traffic for nothing.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  /**
   * Each gesture answers with the room as it now stands, and that answer is
   * written STRAIGHT into the poll's cache. Without it the box would stay
   * unticked for up to 1500 ms after being clicked, which reads as a control
   * that did not work - and the person clicks again.
   */
  const apply = (view: ChatRoomView) => queryClient.setQueryData(chatRoomQueryKey(token), view);
  const consent = useMutation({ mutationFn: () => postChatConsent(token), onSuccess: apply });
  const unconsent = useMutation({ mutationFn: () => deleteChatConsent(token), onSuccess: apply });
  const ready = useMutation({ mutationFn: () => postChatReady(token), onSuccess: apply });
  const unready = useMutation({ mutationFn: () => deleteChatReady(token), onSuccess: apply });

  const surface = chatLobbySurface(room, Date.now());
  const peerRole = room.peer.role;
  const pending = consent.isPending || unconsent.isPending || ready.isPending || unready.isPending;
  const failed = consent.isError || unconsent.isError || ready.isError || unready.isError;

  /**
   * The line beside the button. Silent while the next gesture is this side's
   * own - the box and the live button say it better than a sentence would.
   */
  const hint = !surface.consented
    ? null
    : surface.peer === 'away'
      ? CHAT_PEER_DISCONNECTED[peerRole]
      : surface.peer === 'never-seen'
        ? CHAT_PEER_WAITING[peerRole]
        : null;

  return (
    // `gap-5 py-6` and not `gap-8 py-12` (issue #21): five blocks stacked on
    // the old rhythm put « Je suis prêt » below the fold of a laptop, and the
    // one thing this screen has to do is be pressed.
    <section
      data-testid="chat-lobby"
      data-chat-role={room.role}
      className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-6 py-6"
    >
      <header className="flex flex-col gap-2">
        {room.role === 'interviewer' ? (
          <span className="self-start rounded-full bg-bleu-nuit-tint px-3 py-1 font-semibold text-bleu-nuit-deep text-sm">
            {CHAT_ROLE_LABELS.interviewer}
          </span>
        ) : null}
        <p className="font-semibold text-gray-500 text-sm uppercase tracking-[0.08em]">
          Entretien à l’aveugle
        </p>
        <h1 className="font-bold text-3xl text-noir leading-tight">Avant de commencer</h1>
      </header>

      {consentText.isPending ? (
        <PmsSpinner label="Lecture du consentement" />
      ) : consentText.data === undefined ? (
        <div className="flex flex-col items-start gap-3" data-testid="chat-lobby-consent-failed">
          <p className="text-base text-gray-700">
            Le texte du consentement n’a pas pu être chargé, donc rien ne peut être accepté pour
            l’instant.
          </p>
          <Button
            variant="outline"
            onClick={() => void consentText.refetch()}
            disabled={consentText.isFetching}
          >
            Réessayer
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-base text-gray-700 leading-relaxed">{consentText.data.lead}</p>
          <p className="text-gray-600 text-sm leading-relaxed">{consentText.data.notice}</p>
        </div>
      )}

      <div
        className="flex flex-col gap-2 rounded-md bg-gray-50 p-4"
        data-testid="chat-lobby-presence"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-semibold text-gray-600 text-sm uppercase tracking-[0.08em]">
            Salle d’entretien
          </p>
          <p className="text-gray-500 text-sm" data-testid="chat-lobby-reference">
            Référence {room.reference}
          </p>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-base text-noir">Vous</span>
          <PresenceBadge
            testId="chat-lobby-badge-self"
            label={surface.ready ? 'Connecté, prêt' : 'Connecté'}
            present={true}
          />
        </div>
        {/* A BORDER and not a filled bar: the charte bans the 200 grey and
            darker as a surface, and a one-pixel div is still a surface. */}
        <div className="border-gray-200 border-t" />
        <div className="flex items-center justify-between gap-4">
          <span className="text-base text-noir">{CHAT_ROLE_LABELS[peerRole]}</span>
          <PresenceBadge
            testId="chat-lobby-badge-peer"
            label={
              surface.peer === 'present'
                ? surface.peerReady
                  ? 'Connecté, prêt'
                  : 'Connecté'
                : surface.peer === 'away'
                  ? 'Déconnecté'
                  : 'En attente'
            }
            present={surface.peer === 'present'}
          />
        </div>
      </div>

      {/*
        THE ACTION BLOCK (issue #21): the box that authorises and the button that
        commits, in one frame, at the bottom.

        They used to be separated by the presence panel and by a full-width blue
        notice, so the gesture that greys the button out was a scroll away from
        the button - and you had to go back up to find out why it was dark. The
        « en direct » warning is folded in here as one line rather than an
        encart: it is a condition of pressing the button, so it belongs beside
        it, and as its own block it was pure vertical cost.

        It renders only once the consent text has landed. A checkbox above a
        blank paragraph is not a consent - the rule `routes/index.tsx` already
        holds - which is why the box lives inside this guard rather than beside
        it.
      */}
      {consentText.data === undefined ? null : (
        <div
          className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-md"
          data-testid="chat-lobby-action"
        >
          <label className="flex cursor-pointer items-start gap-3" htmlFor="chat-consent">
            <input
              id="chat-consent"
              type="checkbox"
              className="mt-1 h-5 w-5 flex-none cursor-pointer accent-bleu-nuit"
              checked={surface.consented}
              /*
                Tickable AND un-tickable, until the ready is given (issue #21).

                §4 and §6 used to give the lobby ONE cancellable gesture - the
                ready - and this box was locked the moment it was ticked. At use
                that read as a control that had broken: ticked by accident, there
                was no way back. So the consent became cancellable too, and the
                ORDER of unwinding is what `surface.ready` guards here: a
                `readyAt` standing over a null `consentedAt` is the one state §6
                says cannot exist. Cancel the ready first - the discreet button
                below - then the box.

                The refusal is the SERVER's (409 on `DELETE /consent`), at the
                régime of §4.2; this attribute only spares her a gesture that
                would be refused.
              */
              disabled={surface.ready || pending}
              onChange={() => (surface.consented ? unconsent.mutate() : consent.mutate())}
            />
            <span className="text-base text-noir leading-relaxed">
              {consentText.data.acceptance}
            </span>
          </label>

          {/* One line, and it stays visible: it is a condition of the button
              beside it, not a decoration of the page. */}
          <p className="text-gray-500 text-sm leading-relaxed">
            Cette expérience se déroule en direct : merci de ne pas quitter la conversation avant la
            fin.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            {surface.ready ? (
              <>
                {/* The button KEEPS ITS PLACE and changes its label rather than
                    being replaced by a sentence: §4 forbids the screen receding,
                    and a control that disappears under the cursor is the smallest
                    version of that. */}
                <Button type="button" disabled={true}>
                  {CHAT_PEER_WAITING[peerRole]}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => unready.mutate()}
                  disabled={pending}
                >
                  Annuler mon prêt
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={() => ready.mutate()}
                disabled={!surface.readyEnabled || pending}
              >
                Je suis prêt
              </Button>
            )}
            {hint === null ? null : (
              <span className="text-base text-gray-500" data-testid="chat-lobby-hint">
                {hint}
              </span>
            )}
          </div>
        </div>
      )}

      {failed ? (
        <p role="alert" className="text-base text-rouge-deep">
          Ce geste n’a pas abouti. Réessayez.
        </p>
      ) : null}
    </section>
  );
}

interface PresenceBadgeProps {
  testId: string;
  label: string;
  present: boolean;
}

/**
 * One chair's standing, in two colours and nothing more.
 *
 * Green for present, jaune for everything else - « en attente » and
 * « déconnecté » share a colour on purpose, because they are the same fact for
 * the person reading: the other chair is not usable yet. Only the word tells
 * them apart, and the word is the whole of what §4 asks to be shown.
 */
function PresenceBadge({ testId, label, present }: PresenceBadgeProps) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-semibold text-sm ${
        present ? 'bg-vert-tint text-vert-deep' : 'bg-jaune-tint text-jaune-deep'
      }`}
    >
      {label}
    </span>
  );
}
